import { useMemo } from 'react';

import { Typography } from '@/components';

import { FooterArtwork } from './components/FooterArtwork/FooterArtwork';
import { LogoAndSocials } from './components/LogoAndSocials/LogoAndSocials';
import { MenuSection } from './components/MenuSection/MenuSection';
import { defaultFooterData } from './defaults';
import styles from './Footer.module.scss';

import type { FooterData } from './types';

type FooterProps = {
  data: FooterData;
};

export function Footer({ data }: FooterProps) {
  const brand = data.brand ?? defaultFooterData.brand;

  const sections = useMemo(() => {
    const menuSectionsSource =
      data.menu?.sections && data.menu.sections.length
        ? data.menu.sections
        : defaultFooterData.menu?.sections || [];

    return menuSectionsSource.filter((section) => section.items && section.items.length);
  }, [data.menu]);

  const socials = useMemo(() => {
    const socialItemsSource =
      data.socials && data.socials.length ? data.socials : defaultFooterData.socials || [];

    return socialItemsSource.filter((social) => social.url);
  }, [data.socials]);

  const [primaryMenuSection, ...mainSections] = sections;
  const artwork = data.artwork ?? defaultFooterData.artwork;
  const copyright = data.copyright ?? defaultFooterData.copyright;
  const secondaryColumns = mainSections.slice(0, 3);

  const columnAreaMap = ['secondColumn', 'thirdColumn', 'fourthColumn'];

  const menuColumns = secondaryColumns.map((section, index) => (
    <MenuSection key={section.id} section={section} className={styles[columnAreaMap[index]]} />
  ));

  const primarySection = primaryMenuSection ? (
    <MenuSection section={primaryMenuSection} className={styles.primarySection} />
  ) : null;

  return (
    <div className={styles.footerShell}>
      <div className={styles.content}>
        <LogoAndSocials brand={brand} socials={socials} className={styles.brandColumn} />

        {primarySection}
        {menuColumns}

        <FooterArtwork artwork={artwork} className={styles.artworkColumn} />

        {copyright && (
          <div className={styles.copyrightArea}>
            <Typography variant="body" singleLine color="var(--color-text-default-muted-light)">
              {copyright}
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
}

export default Footer;
